import React, { useState } from 'react';
import { Menu, CanAccess } from 'react-admin';
import { useLocation } from 'react-router-dom';
import {
  Collapse,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  MenuItem,
} from '@mui/material';
import CatalogIcon from '@mui/icons-material/Inventory';
import SwitchAccountIcon from '@mui/icons-material/SwitchAccount';
import ApiIcon from '@mui/icons-material/Code';
import RepairIcon from '@mui/icons-material/Build';
import RouteSharpIcon from '@mui/icons-material/RouteSharp';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import ListAltIcon from '@mui/icons-material/ListAlt';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import AltRouteIcon from '@mui/icons-material/AltRoute';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import SellIcon from '@mui/icons-material/Sell';
import PercentIcon from '@mui/icons-material/Percent';
import TuneIcon from '@mui/icons-material/Tune';
import SyncIcon from '@mui/icons-material/Sync';
import QueueIcon from '@mui/icons-material/Queue';
import { DashboardResources, pluginResourceProps } from '@src/types';

/**
 * Applies active-state color to the Menu.Item icon. react-admin's MenuItemLink
 * sets text.primary on the active item but doesn't propagate to ListItemIcon.
 * This sx targets the icon within the RaMenuItemLink-active class.
 */
const activeIconSx = {
  '&.RaMenuItemLink-active .MuiListItemIcon-root': {
    color: 'text.primary',
  },
};

const SHOPIFY_SYNC_PATHS = ['/shopify-sync'];
const CATALOG_PATHS = [
  `/${DashboardResources.CatalogItems}`,
  `/${DashboardResources.CatalogFieldsMappingUpload}`,
  `/${DashboardResources.ShopifyRedirects}`,
];

const PRICING_PATHS = [
  `/${DashboardResources.PricingRules}`,
  `/${DashboardResources.ItemAdjustedPrices}`,
  `/${DashboardResources.ConditionDiscounts}`,
];

export const CustomMenu = () => {
  const location = useLocation();
  const isOnCatalogPage = CATALOG_PATHS.some(path => location.pathname.startsWith(path));
  const isOnPricingPage = PRICING_PATHS.some(path => location.pathname.startsWith(path));
  const isOnShopifySyncPage = SHOPIFY_SYNC_PATHS.some(path => location.pathname.startsWith(path));

  const [catalogOpen, setCatalogOpen] = useState(isOnCatalogPage);
  const [pricingOpen, setPricingOpen] = useState(isOnPricingPage);
  const [shopifySyncOpen, setShopifySyncOpen] = useState(isOnShopifySyncPage);

  return (
    <Menu>
      <CanAccess resource="partner-select" action="show">
        <Menu.Item
          to="/select-partner"
          primaryText="Partner Select"
          leftIcon={<SwitchAccountIcon />}
        />
      </CanAccess>
      <CanAccess resource={DashboardResources.CatalogItems} action="list">
        <ListItemButton onClick={() => setCatalogOpen(!catalogOpen)}>
          <ListItemIcon sx={{ minWidth: 40 }}>
            <CatalogIcon />
          </ListItemIcon>
          <ListItemText primary="Catalog Management" />
          {catalogOpen ? <ExpandLess /> : <ExpandMore />}
        </ListItemButton>
        <Collapse in={catalogOpen} timeout="auto" unmountOnExit>
          <List component="div" disablePadding>
            <Menu.Item
              to={`/${DashboardResources.CatalogItems}`}
              primaryText="Catalog Items"
              leftIcon={<ListAltIcon />}
              sx={{ pl: 4 }}
            />
            <Menu.Item
              to={`/${DashboardResources.CatalogFieldsMappingUpload}`}
              primaryText="Catalog Fields Mapping Upload"
              leftIcon={<UploadFileIcon />}
              sx={{ pl: 4 }}
            />
            <Menu.Item
              to={`/${DashboardResources.ShopifyRedirects}`}
              primaryText="Shopify Redirects"
              leftIcon={<AltRouteIcon />}
              sx={{ pl: 4 }}
            />
          </List>
        </Collapse>
      </CanAccess>
      {pluginResourceProps.map(({ name, options }) => (
        <CanAccess key={name} action="list" resource={name}>
          <Menu.Item
            to={`/${name}`}
            primaryText={options?.label}
            leftIcon={<RouteSharpIcon />}
          />
        </CanAccess>
      ))}
      <CanAccess resource={DashboardResources.Repairs} action="list">
        <Menu.Item
          to={`/${DashboardResources.Repairs}`}
          primaryText="Repairs Management"
          leftIcon={<RepairIcon />}
        />
      </CanAccess>
      <CanAccess resource={DashboardResources.PricingRules} action="list">
        <MenuItem onClick={() => setPricingOpen(!pricingOpen)}>
          <ListItemIcon sx={{ minWidth: 40 }}>
            <AttachMoneyIcon />
          </ListItemIcon>
          <ListItemText primary="Resale Pricing" />
          {pricingOpen ? <ExpandLess /> : <ExpandMore />}
        </MenuItem>
        <Collapse in={pricingOpen} timeout="auto" unmountOnExit>
          <List component="div" disablePadding>
            <Menu.Item
              to={`/${DashboardResources.PricingRules}`}
              primaryText="Pricing Rules"
              leftIcon={<SellIcon />}
              sx={{ pl: 4, ...activeIconSx }}
            />
            <Menu.Item
              to={`/${DashboardResources.ItemAdjustedPrices}`}
              primaryText="Item Adjusted Prices"
              leftIcon={<TuneIcon />}
              sx={{ pl: 4, ...activeIconSx }}
            />
            <Menu.Item
              to={`/${DashboardResources.ConditionDiscounts}`}
              primaryText="Condition Discounts"
              leftIcon={<PercentIcon />}
              sx={{ pl: 4, ...activeIconSx }}
            />
          </List>
        </Collapse>
      </CanAccess>
      <CanAccess resource="shopify-sync" action="list">
        <ListItemButton onClick={() => setShopifySyncOpen(!shopifySyncOpen)}>
          <ListItemIcon sx={{ minWidth: 40 }}>
            <SyncIcon />
          </ListItemIcon>
          <ListItemText primary="Shopify Sync" />
          {shopifySyncOpen ? <ExpandLess /> : <ExpandMore />}
        </ListItemButton>
        <Collapse in={shopifySyncOpen} timeout="auto" unmountOnExit>
          <List component="div" disablePadding>
            <Menu.Item
              to="/shopify-sync"
              primaryText="Queue Status"
              leftIcon={<QueueIcon />}
              sx={{ pl: 4 }}
            />
          </List>
        </Collapse>
      </CanAccess>
      <CanAccess resource="devtools/api-docs" action="show">
        <Menu.Item
          to="/devtools/api-docs"
          primaryText="API Documentation"
          leftIcon={<ApiIcon />}
        />
      </CanAccess>
    </Menu>
  );
};

export default CustomMenu;
